import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Search, Trash2, Eye, Edit, BookOpen, Download, Folder } from 'lucide-react'
import { useWikipediaStore } from '../../../../stores/wikipediaStore'
import { useFolderStore } from '../../../../stores/folderStore'
import type { SavedWikipedia } from '@creanizer/types'
import { ExportImportButtons } from '../../../../components/common'
import { WikipediaEditModal } from './WikipediaEditModal'
import { FolderManager, FolderMoveSelect } from './FolderManager'

interface SavedWikipediaListProps {
  wikipedias: SavedWikipedia[]
  onLoad: (wikipedia: SavedWikipedia) => void
}

export function SavedWikipediaList({ wikipedias, onLoad }: SavedWikipediaListProps) {
  const { deleteWikipedia, exportAsText, exportAsJSON, exportSingleAsText, exportSingleAsJSON, importFromJSON, clearWikipedias, updateWikipedia } = useWikipediaStore()
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [editingWikipedia, setEditingWikipedia] = useState<SavedWikipedia | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  
  // フォルダによるフィルタリング
  let folderFilteredWikipedias = wikipedias
  if (selectedFolderId === 'all') {
    folderFilteredWikipedias = wikipedias
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('wikipedia')
    folders.forEach(folder => {
      getItemsInFolder('wikipedia', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    folderFilteredWikipedias = wikipedias.filter(item => !allFolderItemIds.has(item.id))
  } else {
    const itemIds = getItemsInFolder('wikipedia', selectedFolderId)
    folderFilteredWikipedias = wikipedias.filter(item => itemIds.includes(item.id))
  }
  
  // 検索によるフィルタリング
  const filteredWikipedias = folderFilteredWikipedias.filter((item) =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.result.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.result.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (item.tags && item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())))
  )

  const handleExportAsJSON = () => {
    exportAsJSON()
  }

  return (
    <div className="card max-w-full overflow-x-hidden">
      <div className="flex items-center gap-2 mb-2">
        <BookOpen className="h-5 w-5" />
        <h2 className="text-xl font-semibold">
          保存済み架空事典 ({wikipedias.length}/500)
        </h2>
      </div>
      <p className="text-muted-foreground mb-6">
        生成した架空事典記事を保存・管理
      </p>
      
      <div className="space-y-4 max-w-full">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="架空事典を検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFolders(!showFolders)}
            className="px-2"
          >
            <Folder className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-2">
          <ExportImportButtons
            onExportText={exportAsText}
            onExportJSON={handleExportAsJSON}
            onImportJSON={importFromJSON}
            dataTypeName="架空事典"
          />

          {wikipedias.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('すべての架空事典を削除しますか？')) {
                  clearWikipedias()
                }
              }}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              全削除
            </Button>
          )}
        </div>

        <div className="flex gap-4">
          {/* フォルダサイドバー */}
          {showFolders && (
            <div className="w-48 border-r pr-4">
              <FolderManager
                generatorType="wikipedia"
                items={wikipedias}
                selectedFolderId={selectedFolderId}
                onFolderSelect={setSelectedFolderId}
                draggedItemId={draggedItemId}
                onItemMove={(itemId, toFolderId) => {
                  const currentFolder = getItemFolder('wikipedia', itemId)
                  moveItemBetweenFolders('wikipedia', itemId, currentFolder?.id || null, toFolderId)
                }}
              />
            </div>
          )}
          
          {/* 架空事典リスト */}
          <div className="flex-1">
            <ScrollArea className="h-[500px] w-full overflow-x-hidden">
              <div className="space-y-2 max-w-full w-full">
                {filteredWikipedias.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {searchQuery ? '検索結果がありません' : '保存済みの架空事典がありません'}
                  </p>
                ) : (
                  filteredWikipedias.map((wikipedia) => (
                    <div 
                      key={wikipedia.id} 
                      className="card p-2 sm:p-4 overflow-hidden max-w-full w-[calc(100%-2rem)] sm:w-full mr-auto cursor-move hover:shadow-lg transition-shadow"
                      draggable
                      onDragStart={(e) => {
                        setDraggedItemId(wikipedia.id)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      onDragEnd={() => {
                        setDraggedItemId(null)
                      }}
                      style={{
                        opacity: draggedItemId === wikipedia.id ? 0.5 : 1
                      }}
                    >
                  {/* モバイル版: 縦並びレイアウト */}
                  <div className="flex flex-col gap-3 sm:hidden w-full">
                    <div className="w-full">
                      <h3 className="font-medium text-sm truncate">{wikipedia.result.title}</h3>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <span className="truncate">{wikipedia.result.category}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                        {wikipedia.result.leadSection}
                      </p>
                      
                      {wikipedia.tags && wikipedia.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {wikipedia.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(wikipedia.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択 */}
                      <div className="mt-2">
                        <FolderMoveSelect
                          generatorType="wikipedia"
                          itemId={wikipedia.id}
                          currentFolderId={getItemFolder('wikipedia', wikipedia.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    {/* モバイル版: 横一列のボタン */}
                    <div className="flex gap-0.5 justify-start w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onLoad(wikipedia)}
                        title="読込"
                        className="w-12 h-7 px-1"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingWikipedia(wikipedia)}
                        title="編集"
                        className="w-12 h-7 px-1"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsText(wikipedia.id)}
                        title="テキスト形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">txt</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsJSON(wikipedia.id)}
                        title="JSON形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">json</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('この架空事典を削除しますか？')) {
                            deleteWikipedia(wikipedia.id)
                          }
                        }}
                        className="w-12 h-7 px-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* PC版: 既存の横並びレイアウト */}
                  <div className="hidden sm:flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{wikipedia.result.title}</h3>
                      </div>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <span className="truncate">{wikipedia.result.category}</span>
                        <span>•</span>
                        <span>{new Date(wikipedia.createdAt).toLocaleDateString('ja-JP')}</span>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                        {wikipedia.result.leadSection}
                      </p>
                      
                      {wikipedia.tags && wikipedia.tags.length > 0 && (
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {wikipedia.tags.map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {wikipedia.memo && (
                        <p className="text-xs text-muted-foreground mt-2 italic">
                          メモ: {wikipedia.memo}
                        </p>
                      )}
                      
                      {/* フォルダ選択（PC版） */}
                      <div className="mt-2 w-32">
                        <FolderMoveSelect
                          generatorType="wikipedia"
                          itemId={wikipedia.id}
                          currentFolderId={getItemFolder('wikipedia', wikipedia.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onLoad(wikipedia)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          読込
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingWikipedia(wikipedia)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          編集
                        </Button>
                      </div>
                      
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsText(wikipedia.id)}
                          title="テキスト形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          txt
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsJSON(wikipedia.id)}
                          title="JSON形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          json
                        </Button>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('この架空事典を削除しますか？')) {
                            deleteWikipedia(wikipedia.id)
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {wikipedias.length >= 450 && (
          <p className="text-xs text-muted-foreground text-center">
            保存可能件数の上限（500件）に近づいています
          </p>
        )}
      </div>
      
      {editingWikipedia && (
        <WikipediaEditModal
          isOpen={!!editingWikipedia}
          onClose={() => setEditingWikipedia(null)}
          onSave={(updatedResult) => {
            updateWikipedia(editingWikipedia.id, {
              result: { ...editingWikipedia.result, ...updatedResult },
              updatedAt: new Date().toISOString()
            })
            setEditingWikipedia(null)
          }}
          wikipediaResult={editingWikipedia.result}
          title={`「${editingWikipedia.result.title}」を編集`}
        />
      )}
    </div>
  )
}